import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import EigooMark from "../components/EigooMark";
import {
    Anchor,
    Cpu,
    ShieldCheck,
    CircleDollarSign,
    Infinity as InfinityIcon,
    ArrowRight,
    Ruler,
    Compass,
} from "lucide-react";

const cormorant = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

/**
 * Four Key Signals
 * (Director-mandated copy — do not rewrite without Director sign-off)
 */
const KEY_SIGNALS = [
    {
        id: "autonomous-provisioning",
        ordinal: "01",
        icon: Cpu,
        eyebrow: "Signal / 01",
        title: "Autonomous Provisioning",
        subtitle: "The site builds itself before you finish the brief.",
        body:
            "Every commissioned site spins up through the EIGOO provisioning layer — compute, storage, DNS, TLS, CDN, and observability all self-bootstrap against sovereign tenancy rules. The operator never waits on infrastructure tickets. The keel is laid in minutes, not quarters.",
        spec: ["0-touch infra", "Per-tenant SSL", "Zero-ticket launch"],
    },
    {
        id: "invisible-security",
        ordinal: "02",
        icon: ShieldCheck,
        eyebrow: "Signal / 02",
        title: "Invisible Security",
        subtitle: "A hardened platform no one has to admire.",
        body:
            "MFA, rate-limiting, key-rotation, audit telemetry, and end-to-end encryption are structural — not features. Operators do not see the armor; adversaries do not see a way in. Security that announces itself is security that has already failed.",
        spec: ["RFC-6238 MFA", "Rotating secrets", "Structural, not bolt-on"],
    },
    {
        id: "zero-cap",
        ordinal: "03",
        icon: InfinityIcon,
        eyebrow: "Signal / 03",
        title: "Zero-Cap",
        subtitle: "No artificial ceiling on reach, records, or revenue.",
        body:
            "Every site is engineered for unbounded horizontal scale at the data, caching, and dispatch tiers. No per-seat pricing traps. No record caps. No throttles on transmission density. The Foundry refuses architectures that punish your success.",
        spec: ["No seat tax", "No record cap", "Horizontal by default"],
    },
    {
        id: "legacy-anchor",
        ordinal: "04",
        icon: Anchor,
        eyebrow: "Signal / 04",
        title: "Legacy Anchor",
        subtitle: "Built to outlive its commissioning operator.",
        body:
            "Every site ships with documented architecture, exportable data, and a Director-signed bond of continuity. The platform is yours — the IP, the database, the DNS. A site built by the Foundry is an heirloom, not a rental.",
        spec: ["Exportable data", "Architecture bond", "Heirloom-grade IP"],
    },
];

const DOCKET = [
    { label: "Division", value: "Sovereign Shipyard" },
    { label: "Signatory", value: "Directorate · Don Kir" },
    { label: "Covenant", value: "80 / 15 / 5 yield" },
    { label: "Tenancy", value: "Multi-subdomain · sovereign" },
    { label: "Turnaround", value: "14-28 days · keel to launch" },
    { label: "Canvas", value: "Celestial · Sovereign" },
];

export default function Foundry() {
    useEffect(() => {
        document.title = "The Foundry · Sovereign Shipyard | EIGOO";
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
            meta = document.createElement("meta");
            meta.name = "description";
            document.head.appendChild(meta);
        }
        meta.content =
            "The EIGOO Foundry is an elite digital shipyard. We don't build websites — we commission digital platforms. Autonomous Provisioning · Invisible Security · Zero-Cap · Legacy Anchor.";
    }, []);

    return (
        <div
            className="min-h-screen bg-background grain-overlay relative overflow-hidden"
            data-testid="foundry-page"
        >
            {/* Blueprint canvas */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
                {/* Blueprint grid */}
                <div
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                        backgroundImage:
                            "linear-gradient(hsl(45 85% 68% / 0.45) 1px, transparent 1px), linear-gradient(90deg, hsl(45 85% 68% / 0.45) 1px, transparent 1px)",
                        backgroundSize: "56px 56px",
                    }}
                />
                {/* Sub-grid (5x denser) */}
                <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage:
                            "linear-gradient(hsl(45 85% 68% / 0.25) 1px, transparent 1px), linear-gradient(90deg, hsl(45 85% 68% / 0.25) 1px, transparent 1px)",
                        backgroundSize: "14px 14px",
                    }}
                />
                {/* Celestial wash */}
                <div className="absolute top-[-18%] right-[-12%] w-[60%] h-[60%] rounded-full bg-[hsl(40_60%_45%/0.08)] blur-[140px]" />
                <div className="absolute bottom-[-20%] left-[-12%] w-[55%] h-[55%] rounded-full bg-[hsl(153_50%_25%/0.18)] blur-[140px]" />
                {/* Corner schematic brackets */}
                {["tl", "tr", "bl", "br"].map((c) => (
                    <SchematicBracket key={c} position={c} />
                ))}
            </div>

            {/* Navbar */}
            <header className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
                <Link to="/" data-testid="foundry-logo-link">
                    <EigooMark size="lg" sub="The Foundry · Sovereign Shipyard" testid="foundry-mark" />
                </Link>
                <div className="flex items-center gap-5 text-[10px] uppercase tracking-[0.3em]">
                    <Link
                        to="/intel"
                        className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                        data-testid="foundry-nav-intel"
                    >
                        Intel Hub
                    </Link>
                    <Link
                        to="/foundry/control"
                        className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                        data-testid="foundry-nav-dispatch"
                    >
                        Dispatch
                    </Link>
                    <Link
                        to="/login"
                        className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                        data-testid="foundry-nav-login"
                    >
                        Bridge
                    </Link>
                </div>
            </header>

            {/* Hero / Title block */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 pt-8 pb-14">
                <div className="grid lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-8">
                        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-[hsl(45_85%_68%)] mb-5">
                            <span className="inline-flex items-center gap-1.5">
                                <Compass size={11} /> Sheet 01 · Sovereign Shipyard
                            </span>
                            <span className="h-px flex-1 bg-[hsl(40_40%_25%/0.5)]" />
                            <span className="font-mono">EIGOO-SHP-2026.02</span>
                        </div>

                        <h1
                            className="font-heading tracking-tighter leading-[0.92]"
                            style={{ fontSize: "clamp(3rem, 7vw, 6rem)" }}
                            data-testid="foundry-headline"
                        >
                            The EIGOO Foundry is an{" "}
                            <span className="gold-gradient-text">elite digital shipyard.</span>
                        </h1>

                        <p
                            className="mt-7 text-2xl sm:text-3xl leading-snug text-foreground/90 max-w-3xl"
                            style={{ ...cormorant, fontStyle: "italic" }}
                            data-testid="foundry-lede"
                        >
                            "We don't build websites. We commission digital platforms."
                        </p>
                        <p className="mt-5 max-w-2xl text-base text-muted-foreground leading-relaxed">
                            Every platform forged in the Foundry is engineered against four immutable signals —
                            the Sovereign covenant by which each site is laid, armored, scaled, and passed
                            down. No templates. No rentals. No ceilings.
                        </p>
                    </div>

                    {/* Docket / blueprint title-block */}
                    <aside
                        className="lg:col-span-4 border border-[hsl(40_40%_25%/0.45)] rounded-none bg-[hsl(151_55%_7%/0.75)] backdrop-blur-sm"
                        data-testid="foundry-docket"
                    >
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[hsl(40_40%_25%/0.45)] text-[9px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)]">
                            <span>Docket</span>
                            <span className="font-mono">SHP-01 / REV · A</span>
                        </div>
                        <dl className="divide-y divide-[hsl(40_40%_25%/0.3)]">
                            {DOCKET.map((d) => (
                                <div key={d.label} className="flex items-center justify-between px-4 py-2.5">
                                    <dt className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                                        {d.label}
                                    </dt>
                                    <dd className="text-xs text-foreground/90 font-mono text-right">
                                        {d.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                        <div className="px-4 py-3 border-t border-[hsl(40_40%_25%/0.45)] text-[9px] uppercase tracking-[0.35em] text-muted-foreground flex items-center gap-2">
                            <Ruler size={11} className="text-[hsl(45_85%_68%)]" />
                            Drawn to sovereign scale · not to contract
                        </div>
                    </aside>
                </div>
            </section>

            {/* Four Key Signals — blueprint grid */}
            <section
                className="relative z-10 max-w-7xl mx-auto px-6 pb-20"
                data-testid="foundry-signals"
            >
                <div className="flex items-center gap-4 mb-8">
                    <div className="h-px flex-1 bg-[hsl(40_40%_25%/0.5)]" />
                    <div className="text-[10px] uppercase tracking-[0.45em] text-[hsl(45_85%_68%)] font-mono">
                        Four Key Signals · Sovereign Covenant
                    </div>
                    <div className="h-px flex-1 bg-[hsl(40_40%_25%/0.5)]" />
                </div>

                <div className="grid md:grid-cols-2 gap-0 border border-[hsl(40_40%_25%/0.45)]">
                    {KEY_SIGNALS.map((s, i) => (
                        <SignalPanel
                            key={s.id}
                            signal={s}
                            // Top-row panels have no top border; left column has no left border.
                            borderClasses={[
                                i >= 2 ? "border-t border-[hsl(40_40%_25%/0.45)]" : "",
                                i % 2 === 1 ? "border-l border-[hsl(40_40%_25%/0.45)]" : "",
                            ].join(" ")}
                        />
                    ))}
                </div>

                {/* Commission CTA — plate */}
                <div
                    className="mt-12 border border-[hsl(40_40%_25%/0.5)] bg-[hsl(151_55%_7%/0.7)] backdrop-blur-sm p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6"
                    data-testid="foundry-cta"
                >
                    <div className="max-w-2xl">
                        <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] font-mono mb-3">
                            Commission Order · available upon review
                        </div>
                        <div className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight">
                            Lay the keel of your site.
                        </div>
                        <p className="mt-3 text-sm text-muted-foreground max-w-xl">
                            The Foundry accepts a limited roster of commissions each quarter. Submit the
                            charter brief — the Directorate reviews within one business day and routes a
                            Sovereign covenant back to you.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Link
                            to="/foundry/control"
                            data-testid="foundry-cta-dispatch"
                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        >
                            File a charter brief <ArrowRight size={16} />
                        </Link>
                        <Link
                            to="/property/foundry-listing-01"
                            data-testid="foundry-cta-spf"
                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-[hsl(40_40%_25%/0.6)] text-foreground/90 hover:border-[hsl(45_85%_68%)] hover:text-[hsl(45_85%_75%)] transition-colors"
                        >
                            <CircleDollarSign size={16} /> Property Foundry
                        </Link>
                    </div>
                </div>

                {/* Footer stamp */}
                <div className="mt-10 flex items-center justify-between text-[9px] uppercase tracking-[0.4em] text-muted-foreground font-mono">
                    <span>Drawn by Directorate · Sheet 01 of 01</span>
                    <span>Revision A · 2026.02</span>
                </div>
            </section>
        </div>
    );
}

function SignalPanel({ signal, borderClasses }) {
    const Icon = signal.icon;
    return (
        <article
            className={`relative group p-7 lg:p-9 ${borderClasses}`}
            data-testid={`foundry-signal-${signal.id}`}
        >
            {/* Ordinal */}
            <div className="flex items-start justify-between">
                <div className="text-[9px] uppercase tracking-[0.45em] text-[hsl(45_85%_68%)] font-mono">
                    {signal.eyebrow}
                </div>
                <div
                    className="font-heading gold-gradient-text opacity-30 group-hover:opacity-70 transition-opacity"
                    style={{ fontSize: "4.25rem", lineHeight: 1, letterSpacing: "-0.04em" }}
                    aria-hidden="true"
                >
                    {signal.ordinal}
                </div>
            </div>

            {/* Icon medallion */}
            <div className="mt-5 inline-flex items-center justify-center h-12 w-12 rounded-full border border-[hsl(40_40%_25%/0.6)] bg-[hsl(151_55%_9%/0.8)]">
                <Icon size={18} className="text-[hsl(45_85%_68%)]" />
            </div>

            {/* Title */}
            <h3 className="mt-5 font-heading text-3xl tracking-tight leading-tight">
                {signal.title}
            </h3>
            <p
                className="mt-2 text-lg text-foreground/80"
                style={{ ...cormorant, fontStyle: "italic" }}
            >
                {signal.subtitle}
            </p>

            {/* Body */}
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-xl">
                {signal.body}
            </p>

            {/* Tech-spec line */}
            <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.25em] text-[hsl(45_85%_68%)] font-mono border-t border-[hsl(40_40%_25%/0.3)] pt-4">
                {signal.spec.map((s) => (
                    <span key={s} className="flex items-center gap-1.5">
                        <span className="h-1 w-1 rounded-full bg-[hsl(45_85%_68%)]" />
                        {s}
                    </span>
                ))}
            </div>

            {/* Hairline annotations (blueprint corners) */}
            <span aria-hidden="true" className="absolute top-0 left-0 h-3 w-3 border-t border-l border-[hsl(45_85%_68%/0.5)]" />
            <span aria-hidden="true" className="absolute top-0 right-0 h-3 w-3 border-t border-r border-[hsl(45_85%_68%/0.5)]" />
            <span aria-hidden="true" className="absolute bottom-0 left-0 h-3 w-3 border-b border-l border-[hsl(45_85%_68%/0.5)]" />
            <span aria-hidden="true" className="absolute bottom-0 right-0 h-3 w-3 border-b border-r border-[hsl(45_85%_68%/0.5)]" />
        </article>
    );
}

function SchematicBracket({ position }) {
    const base = "absolute h-24 w-24 opacity-50";
    const map = {
        tl: "top-6 left-6 border-t border-l",
        tr: "top-6 right-6 border-t border-r",
        bl: "bottom-6 left-6 border-b border-l",
        br: "bottom-6 right-6 border-b border-r",
    };
    return <span aria-hidden="true" className={`${base} ${map[position]} border-[hsl(45_85%_68%/0.35)]`} />;
}
